import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import LearningDetailCard from '@/components/organisms/Card/LearningDetailCard';
import { adminStudentApi } from '@/services/api';
import { Spin, Typography, Button } from 'antd';

type UnitProgress = {
  id: number;
  code?: string;
  title: string;
  unitType?: string;
  unitCategory?: string;
  orderIndex?: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  progress: number;
  score?: number;
};

const { Title, Text } = Typography;

export default function AdminStudentScheduleDetail() {
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const category = (searchParams.get('category') || 'learning') as 'learning' | 'homework';
  const studentId = searchParams.get('studentId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [units, setUnits] = useState<UnitProgress[]>([]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!scheduleId) return;
      setLoading(true);
      setError('');
      try {
        const res: any = await adminStudentApi.scheduleDetail(Number(scheduleId));
        const data = res?.data || res;
        setTitle(String(data?.scheduleTitle ?? ''));
        setStartDate(data?.startDate ? String(data.startDate).slice(0, 10) : undefined);
        setEndDate(data?.endDate ? String(data.endDate).slice(0, 10) : undefined);
        setProgress(Number(data?.progress ?? 0));
        const textbooks = Array.isArray(data?.textbooks) ? data.textbooks : [];
        const flat: UnitProgress[] = [];
        textbooks.forEach((tb: any) => {
          const u = Array.isArray(tb?.units) ? tb.units : [];
          u.forEach((it: any, idx: number) => {
            flat.push({
              id: Number(it.unitId ?? it.id ?? idx),
              code: String(it.unitCode ?? ''),
              title: String(it.unitTitle ?? ''),
              unitType: String(it.unitType ?? ''),
              unitCategory: String(it.unitCategory ?? ''),
              orderIndex: Number(it.orderIndex ?? idx),
              status: String(it.status ?? 'NOT_STARTED').toUpperCase() as UnitProgress['status'],
              progress: Number(it.progress ?? 0),
              score: it.score != null ? Number(it.score) : undefined,
            });
          });
        });
        setUnits(flat);
      } catch (e: any) {
        setError(e?.response?.data?.message || '상세 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [scheduleId]);

  const status = useMemo<'completed' | 'in-progress' | 'waiting'>(() => {
    if (progress >= 100) return 'completed';
    const anyProgress = units.some((u) => u.progress > 0 && u.progress < 100) || progress > 0;
    return anyProgress ? 'in-progress' : 'waiting';
  }, [progress, units]);

  const handleBack = () => {
    if (studentId) {
      navigate(`/admin/student/detail/${studentId}?tab=learning&category=${category}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <AdminLayout>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin tip="상세 정보를 불러오는 중..." />
        </div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
            {error}
          </Text>
          <Button type="link" onClick={handleBack}>
            뒤로가기
          </Button>
        </div>
      ) : (
        <div style={{ padding: 24 }}>
          <Button type="link" onClick={handleBack} style={{ paddingLeft: 0 }}>
            ← 뒤로가기
          </Button>
          <Title level={2} style={{ marginTop: 0 }}>
            {title}
          </Title>
          {(startDate || endDate) && (
            <Text type="secondary">
              {startDate || ''}
              {endDate ? ` ~ ${endDate}` : ''}
            </Text>
          )}

          <div style={{ marginTop: 24 }}>
            <LearningDetailCard
              status={status}
              progress={progress}
              type={category}
              units={units}
              scheduleId={Number(scheduleId)}
              disableNavigation
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

